import * as Sentry from "@sentry/react";
import { isSupabaseConfigured } from "./supabase";

/**
 * Sentry DSN from environment variables
 */
const sentryDsn =
  import.meta.env.VITE_SENTRY_DSN ||
  "https://1c3f7fca5d2d3fd9c8bd175973799ec2@o4510402651947008.ingest.de.sentry.io/4510419704545360";

/**
 * Release version from environment (commit SHA in production)
 */
const release =
  import.meta.env.COMMIT_SHA || import.meta.env.MODE || "development";

/**
 * Check if Sentry is configured
 */
export const isSentryConfigured = Boolean(
  sentryDsn && sentryDsn.trim() !== "" && sentryDsn.startsWith("http"),
);

/**
 * Check if we're running on production domain (app.ayocruz.com)
 */
const isProductionDomain = (): boolean => {
  if (typeof window === "undefined") return false;
  const hostname = window.location.hostname;
  return hostname === "app.ayocruz.com" || hostname.includes("ayocruz.com");
};

/**
 * Check if Sentry should be enabled
 * Only enable in production mode OR on production domain (app.ayocruz.com)
 */
const shouldEnableSentry = (): boolean => {
  const isProduction = import.meta.env.MODE === "production";
  const isProdDomain = isProductionDomain();

  // Enable only in production mode OR on production domain
  return isProduction || isProdDomain;
};

/**
 * Initialize Sentry error tracking
 * This should be called once at application startup
 * Only enabled in production or on app.ayocruz.com domain
 *
 * Note: Ad blockers may block requests to Sentry (net::ERR_BLOCKED_BY_CLIENT).
 * That is expected; the app works normally, only error reporting is not sent.
 */
export function initSentry(): void {
  if (!isSentryConfigured) {
    console.warn(
      "⚠️ Sentry not configured. Error tracking disabled. Set VITE_SENTRY_DSN in .env file",
    );
    return;
  }

  // Disable Sentry in local development (localhost)
  if (!shouldEnableSentry()) {
    console.log(
      "ℹ️ Sentry disabled in local development. Only enabled in production (app.ayocruz.com)",
    );
    return;
  }

  // Suppress console errors for blocked Sentry requests (ad blockers)
  // This prevents ERR_BLOCKED_BY_CLIENT errors from cluttering the console
  if (typeof window !== "undefined") {
    // Override console.error to filter out Sentry-related blocked requests
    const originalConsoleError = console.error;
    console.error = (...args: unknown[]): void => {
      const message = String(args[0] || "");
      if (
        message.includes("ERR_BLOCKED_BY_CLIENT") &&
        (message.includes("sentry.io") ||
          args.some((arg) => String(arg).includes("sentry.io")))
      ) {
        // Silently ignore - ad blocker is blocking Sentry, which is expected
        return;
      }
      originalConsoleError.apply(console, args);
    };

    // Handle window.onerror
    const originalError = window.onerror;
    window.onerror = (
      message: string | Event,
      source?: string,
      lineno?: number,
      colno?: number,
      error?: Error,
    ): boolean => {
      const messageStr = String(message);
      if (
        messageStr.includes("ERR_BLOCKED_BY_CLIENT") &&
        (messageStr.includes("sentry.io") || source?.includes("sentry.io"))
      ) {
        // Silently ignore - ad blocker is blocking Sentry, which is expected
        return true; // Prevent default error handling
      }
      if (originalError) {
        return originalError(message, source, lineno, colno, error);
      }
      return false;
    };

    // Handle unhandled promise rejections
    window.addEventListener("unhandledrejection", (event) => {
      const reason = String(event.reason || "");
      if (
        reason.includes("ERR_BLOCKED_BY_CLIENT") &&
        reason.includes("sentry.io")
      ) {
        event.preventDefault(); // Prevent error from being logged
      }
    });
  }

  const isProdDomain = isProductionDomain();

  try {
    // Set environment based on domain
    const environment = isProdDomain
      ? "production"
      : import.meta.env.MODE || "development";

    Sentry.init({
      dsn: sentryDsn,
      environment: environment,
      release: release,
      integrations: [
        // Tracing disabled - browserTracingIntegration causes js-profiling violation
        // Use only basic error tracking without performance monitoring
        // Sentry.browserTracingIntegration(),
        // Profiling disabled - causes Document policy violation (js-profiling not allowed)
        // Sentry.browserProfilingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
        // Send all console logs in production
        Sentry.consoleLoggingIntegration({
          levels: ["log", "warn", "error"],
        }),
      ],
      // Enable logs to be sent to Sentry
      enableLogs: true,
      // Ignore errors caused by ad blockers blocking Sentry requests
      ignoreErrors: [
        "net::ERR_BLOCKED_BY_CLIENT",
        "Failed to fetch",
        "NetworkError",
        "Load failed",
      ],
      // Performance Monitoring - Tracing disabled (causes js-profiling violation)
      // Set to 0 to completely disable tracing
      tracesSampleRate: 0,
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [
        /^https:\/\/app\.ayocruz\.com/,
        /^https:\/\/.*\.supabase\.co/,
        /^https:\/\/.*\.supabase\.io/,
      ],
      // Profiling disabled - causes Document policy violation (js-profiling not allowed)
      // Set profilesSampleRate to 0 to disable profiling
      profilesSampleRate: 0,
      // Session Replay
      // 10% of sessions
      replaysSessionSampleRate: 0.1,
      // 100% on error
      replaysOnErrorSampleRate: 1.0,
      // Setting this option to true will send default PII data to Sentry.
      // For example, automatic IP address collection on events
      sendDefaultPii: true,
      // Set user context if Supabase is configured
      beforeSend(event) {
        // Don't send errors in demo mode when Supabase is not configured
        if (!isSupabaseConfigured) {
          return null;
        }
        // Filter out errors caused by ad blockers blocking Sentry
        if (event.exception) {
          const errorMessage = event.exception.values?.[0]?.value || "";
          if (
            errorMessage.includes("ERR_BLOCKED_BY_CLIENT") ||
            errorMessage.includes("Failed to fetch") ||
            errorMessage.includes("NetworkError")
          ) {
            return null;
          }
        }
        return event;
      },
      // Suppress 429 errors and blocked requests in console
      beforeBreadcrumb(breadcrumb) {
        // Filter out breadcrumbs related to rate limiting or blocked requests
        if (breadcrumb.category === "xhr" || breadcrumb.category === "fetch") {
          const url = breadcrumb.data?.url as string | undefined;
          if (
            url?.includes("sentry.io") &&
            (url?.includes("429") ||
              breadcrumb.data?.statusCode === 0 ||
              breadcrumb.data?.error?.message?.includes(
                "ERR_BLOCKED_BY_CLIENT",
              ))
          ) {
            return null;
          }
        }
        return breadcrumb;
      },
    });

    console.log("✅ Sentry initialized successfully");
  } catch (error) {
    console.error("❌ Failed to initialize Sentry:", error);
  }
}

/**
 * Set user context for error tracking
 * Call this after user authentication
 */
export function setSentryUser(userId: string, email?: string): void {
  if (!isSentryConfigured) return;

  Sentry.setUser({
    id: userId,
    email: email,
  });
}

/**
 * Clear user context
 * Call this on logout
 */
export function clearSentryUser(): void {
  if (!isSentryConfigured) return;

  Sentry.setUser(null);
}

/**
 * Manually capture an exception
 */
export function captureException(
  error: Error,
  context?: Record<string, unknown>,
): void {
  if (!isSentryConfigured) {
    console.error("Error (Sentry not configured):", error, context);
    return;
  }

  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Manually capture a message
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = "info",
): void {
  if (!isSentryConfigured) {
    console.log(`[${level.toUpperCase()}] ${message}`);
    return;
  }

  Sentry.captureMessage(message, level);
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(
  message: string,
  category?: string,
  level: Sentry.SeverityLevel = "info",
  data?: Record<string, unknown>,
): void {
  if (!isSentryConfigured) return;

  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
  });
}

/**
 * Track a count metric (increment counter)
 */
export function trackCount(metricName: string, value: number = 1): void {
  if (!isSentryConfigured) return;

  Sentry.metrics.count(metricName, value);
}

/**
 * Track a gauge metric (current value at a point in time)
 */
export function trackGauge(metricName: string, value: number): void {
  if (!isSentryConfigured) return;

  Sentry.metrics.gauge(metricName, value);
}

/**
 * Track a distribution metric (statistical distribution of values)
 */
export function trackDistribution(metricName: string, value: number): void {
  if (!isSentryConfigured) return;

  Sentry.metrics.distribution(metricName, value);
}
